/**
 * Renders the widget chart from normalized items using Recharts.
 * Chart type: bar | pie | donut | area | radar.
 */
import { Box, Skeleton, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { getChartColors } from './chartColors';
import { ReportWidgetAreaChart } from './charts/ReportWidgetAreaChart';
import { ReportWidgetBarChart } from './charts/ReportWidgetBarChart';
import { ReportWidgetLineChart } from './charts/ReportWidgetLineChart';
import { ReportWidgetPieChart } from './charts/ReportWidgetPieChart';
import { ReportWidgetRadarChart } from './charts/ReportWidgetRadarChart';

import {
    type ReportWidgetChartPoint,
    type ReportWidgetChartType,
    type ReportWidgetItem,
    type ReportWidgetVisualizationOptions,
    getChartItemId,
} from '../model/types';

const CHART_HEIGHT = 280;

function itemToChartPoint(item: ReportWidgetItem): ReportWidgetChartPoint {
    const num =
        typeof item.primaryValue === 'number'
            ? item.primaryValue
            : parseFloat(String(item.primaryValue));
    return {
        name: item.label,
        value: Number.isNaN(num) ? 0 : num,
        id: getChartItemId(item),
        color: item.color,
    };
}

function formatValue(
    value: number | string,
    formatter?: (v: number | string) => string,
): string {
    if (formatter) return formatter(value);
    if (typeof value === 'number')
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    return String(value);
}

interface ReportWidgetChartProps {
    items: ReportWidgetItem[];
    chartType: ReportWidgetChartType;
    /** Presentation options (legend, formatter, colors). Aligns with config.visualization.options. */
    options?: ReportWidgetVisualizationOptions;
    title?: string;
    loading?: boolean;
    disableAnimation?: boolean;
    makeSticky?: boolean;
    onItemClick?: (item: ReportWidgetItem) => void;
    activeItemId?: string;
    onItemHover?: (item: ReportWidgetItem | null) => void;
}

export const ReportWidgetChart = memo(
    ({
        items,
        chartType,
        options,
        title,
        loading = false,
        disableAnimation = false,
        makeSticky = false,
        onItemClick,
        activeItemId,
        onItemHover,
    }: ReportWidgetChartProps) => {
        const { t } = useTranslation();
        const {
            showLegend = true,
            valueFormatter,
            colors,
            palette,
            lineNames = ['Series 1', 'Series 2'],
            secondSeriesKey = 'value2',
            xAxisTickFormatter,
        } = options ?? {};

        const paletteColors = getChartColors({ palette, colors });

        const data = useMemo(
            () =>
                items.map(itemToChartPoint).map((p, i) => ({
                    ...p,
                    color: p.color ?? paletteColors[i % paletteColors.length],
                })),
            [items, paletteColors],
        );

        const total = useMemo(
            () => data.reduce((sum, point) => sum + point.value, 0),
            [data],
        );

        if (loading) {
            return (
                <Skeleton
                    variant="rectangular"
                    height={CHART_HEIGHT}
                    sx={{ width: '100%', borderRadius: '12px' }}
                />
            );
        }

        if (data.length === 0) {
            return (
                <Box
                    sx={{
                        height: CHART_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '12px',
                        backgroundColor: 'action.hover',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('composableReportWidget.noData')}
                    </Typography>
                </Box>
            );
        }

        const tooltipFormatter = (value: number) =>
            formatValue(value, valueFormatter);

        const chartHeight = CHART_HEIGHT - (title ? 36 : 0);

        let chartContent: JSX.Element | null = null;

        switch (chartType) {
            case 'bar':
                chartContent = (
                    <ReportWidgetBarChart
                        data={data}
                        showLegend={showLegend}
                        tooltipFormatter={tooltipFormatter}
                        items={items}
                        total={total}
                        onItemClick={onItemClick}
                        activeItemId={activeItemId}
                        disableAnimation={disableAnimation}
                        onItemHover={onItemHover}
                    />
                );
                break;
            case 'pie':
            case 'donut':
                chartContent = (
                    <ReportWidgetPieChart
                        data={data}
                        showLegend={showLegend}
                        tooltipFormatter={tooltipFormatter}
                        items={items}
                        total={total}
                        onItemClick={onItemClick}
                        variant={chartType}
                        activeItemId={activeItemId}
                        disableAnimation={disableAnimation}
                        minAngle={options?.pieMinAngle ?? 4}
                    />
                );
                break;
            case 'radar':
                chartContent = (
                    <ReportWidgetRadarChart
                        data={data}
                        showLegend={showLegend}
                        tooltipFormatter={tooltipFormatter}
                        items={items}
                        total={total}
                        disableAnimation={disableAnimation}
                    />
                );
                break;
            case 'area':
                chartContent = (
                    <ReportWidgetAreaChart
                        data={data}
                        showLegend={showLegend}
                        tooltipFormatter={tooltipFormatter}
                        items={items}
                        total={total}
                        disableAnimation={disableAnimation}
                    />
                );
                break;
            case 'line': {
                const lineData = items.map((item) => {
                    const v1 =
                        typeof item.primaryValue === 'number'
                            ? item.primaryValue
                            : parseFloat(String(item.primaryValue));
                    const v2Raw = item.secondaryValues?.[secondSeriesKey];
                    const v2 =
                        typeof v2Raw === 'number'
                            ? v2Raw
                            : parseFloat(String(v2Raw ?? 0));
                    return {
                        name: item.label,
                        id: item.id,
                        value1: Number.isNaN(v1) ? 0 : v1,
                        value2: Number.isNaN(v2) ? 0 : v2,
                    };
                });
                chartContent = (
                    <ReportWidgetLineChart
                        data={lineData}
                        showLegend={showLegend}
                        tooltipFormatter={tooltipFormatter}
                        lineNames={lineNames}
                        xAxisTickFormatter={xAxisTickFormatter}
                        disableAnimation={disableAnimation}
                    />
                );
                break;
            }
            default:
                chartContent = null;
                console.error(`Unsupported chart type: ${chartType}`);
                break;
        }

        return (
            <Box
                data-testclass="composable-report-widget-chart"
                sx={{
                    width: '100%',
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                    position: makeSticky ? 'sticky' : undefined,
                    top: makeSticky ? 0 : undefined,
                }}
            >
                {title && (
                    <Typography
                        variant="subtitle2"
                        sx={{
                            marginBottom: 1,
                            marginLeft: 1,
                        }}
                    >
                        {title}
                    </Typography>
                )}
                <ResponsiveContainer
                    width="99%"
                    height={chartHeight}
                    minWidth={0}
                >
                    {chartContent}
                </ResponsiveContainer>
            </Box>
        );
    },
);

ReportWidgetChart.displayName = 'ReportWidgetChart';
